// ============================================================
// AgentMailDraftCard — メール下書きカード（2026-09-01）
//
// 正: booking-platform/md/ai-control-center.md §16-6u
//
// 起案（draft_reservation_email）の直後にチャットへ出る。宛先・件名・本文を
// **改行つきのまま**見せ、[送信][編集][破棄] の3ボタンで人が決める。
// このボタンが人の承認そのものなので、従来の送信確認カード（同じ内容を
// 二度見せていた）は廃止した（前田さん 2026-09-01）。
// ============================================================
import React, { useState } from 'react';
import { Box, Button, Chip, TextField, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

interface Props {
  to: string;
  subject: string;
  body: string;
  replyMode?: 'thread' | 'new';
  state: 'pending' | 'sent' | 'discarded';
  note?: string;
  busy?: boolean;
  onSend?: (body: string) => void;
  onDiscard?: () => void;
  onEditSave?: (body: string) => void;
}

export default function AgentMailDraftCard({
  to, subject, body, replyMode, state, note, busy, onSend, onDiscard, onEditSave,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(body);

  const done = state !== 'pending';
  const headColor = state === 'sent' ? '#059669' : state === 'discarded' ? '#64748b' : '#2563eb';
  const headLabel = state === 'sent' ? '✅ 送信しました'
    : state === 'discarded' ? '🗑 破棄しました' : '✉️ メールの下書き';

  return (
    <Box sx={{ px: 1.5, my: 0.8 }}>
      <Box sx={{ border: '1px solid #dbeafe', borderRadius: 2, overflow: 'hidden', background: '#fff' }}>
        <Box sx={{ background: headColor, color: '#fff', px: 1.4, py: 0.7,
                   display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ fontSize: 13 }}>{headLabel}</Typography>
          {replyMode && state === 'pending' && (
            <Chip size="small" label={replyMode === 'thread' ? 'スレッド返信' : '新規メール'}
              sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(255,255,255,.22)', color: '#fff' }} />
          )}
        </Box>

        <Box sx={{ px: 1.4, py: 1 }}>
          <Row label="宛先" value={to || '(予約データから決定)'} />
          <Row label="件名" value={subject} />

          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.8, mb: 0.3 }}>
            本文
          </Typography>
          {editing ? (
            <TextField
              fullWidth multiline minRows={8} maxRows={20} size="small"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              sx={{ '& .MuiInputBase-input': { fontSize: 12, lineHeight: 1.65, fontFamily: 'inherit' } }}
            />
          ) : (
            <Box sx={{
              fontSize: 12, lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1,
              px: 1, py: 0.8, maxHeight: 320, overflowY: 'auto',
              color: done ? '#64748b' : '#0f172a',
            }}>
              {draft}
            </Box>
          )}

          {note && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.8, color: state === 'sent' ? '#059669' : '#b45309' }}>
              {note}
            </Typography>
          )}

          {state === 'pending' && (
            <Box sx={{ display: 'flex', gap: 0.7, mt: 1, flexWrap: 'wrap' }}>
              {editing ? (
                <>
                  <Button size="small" variant="contained" disabled={busy}
                    onClick={() => { onEditSave?.(draft); setEditing(false); }}
                    sx={{ fontSize: 12 }}>
                    編集を保存
                  </Button>
                  <Button size="small" variant="outlined" color="inherit" disabled={busy}
                    onClick={() => { setDraft(body); setEditing(false); }}
                    sx={{ fontSize: 12 }}>
                    取り消す
                  </Button>
                </>
              ) : (
                <>
                  <Button size="small" variant="contained" startIcon={<SendIcon sx={{ fontSize: 15 }} />}
                    disabled={busy} onClick={() => onSend?.(draft)}
                    sx={{ fontSize: 12, fontWeight: 700 }}>
                    送信
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                    disabled={busy} onClick={() => setEditing(true)}
                    sx={{ fontSize: 12 }}>
                    編集
                  </Button>
                  <Button size="small" variant="outlined" color="inherit"
                    startIcon={<DeleteOutlineIcon sx={{ fontSize: 15 }} />}
                    disabled={busy} onClick={() => onDiscard?.()}
                    sx={{ fontSize: 12, color: '#64748b', borderColor: '#cbd5e1' }}>
                    破棄
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mb: 0.3 }}>
      <Typography variant="caption" sx={{ color: '#64748b', minWidth: 34, flexShrink: 0 }}>{label}</Typography>
      <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 600, wordBreak: 'break-all' }}>
        {value}
      </Typography>
    </Box>
  );
}
